// ============================================
// FILE: frontend/src/api/connections.api.js
// PURPOSE: API calls for connection management
// ============================================

import apiClient from './client.js';

console.log('[connections.api] Initializing connections API');

// ============================================
// SECTION: CREATE CONNECTION (ADMIN)
// ============================================

/**
 * Admin connects two parties after approving a match
 * @param {string} matchId - Match ID to connect
 * @param {string} adminNotes - Optional notes
 */
export const connectParties = async (matchId, adminNotes = '') => {
  console.log('[connections.api] 🔗 Calling: POST /api/connections/connect', {
    matchId,
    notesLength: adminNotes.length
  });

  try {
    const response = await apiClient.post('/connections/connect', {
      matchId,
      adminNotes
    });
    console.log('[connections.api] ✅ Parties connected:', {
      connectionId: response.data.connection?.id,
      landowner: response.data.connection?.landowner.email,
      investor: response.data.connection?.investor.email
    });
    return response.data;
  } catch (error) {
    console.error('[connections.api] ❌ Failed to connect parties:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH ALL CONNECTIONS (ADMIN)
// ============================================

/**
 * Get all connections with pagination and filtering
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @param {string} status - Filter by status (all, connected, both_contacted, deal_in_progress, deal_completed, deal_failed)
 * @param {string} sortBy - Sort order (newest, oldest, recent-activity)
 */
export const getAllConnections = async (page = 1, limit = 20, status = 'all', sortBy = 'newest') => {
  console.log('[connections.api] 📋 Calling: GET /api/connections/all', {
    page,
    limit,
    status,
    sortBy
  });

  try {
    const response = await apiClient.get('/connections/all', {
      params: { page, limit, status, sortBy }
    });
    console.log('[connections.api] ✅ Connections fetched:', {
      count: response.data.connections?.length,
      total: response.data.pagination?.total
    });
    return response.data;
  } catch (error) {
    console.error('[connections.api] ❌ Failed to fetch connections:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH SINGLE CONNECTION
// ============================================

/**
 * Get detailed connection information
 * @param {string} connectionId - Connection ID
 */
export const getConnectionDetails = async (connectionId) => {
  console.log('[connections.api] 🔍 Calling: GET /api/connections/:id', { connectionId });

  try {
    const response = await apiClient.get(`/connections/${connectionId}`);
    console.log('[connections.api] ✅ Connection details retrieved:', {
      status: response.data.connection?.status,
      matchScore: response.data.connection?.matchScore
    });
    return response.data;
  } catch (error) {
    console.error('[connections.api] ❌ Failed to fetch connection:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: UPDATE CONNECTION STATUS (ADMIN)
// ============================================

/**
 * Admin updates connection status
 * Statuses: connected, both_contacted, deal_in_progress, deal_completed, deal_failed
 * @param {string} connectionId - Connection ID
 * @param {string} status - New status
 * @param {string} adminNotes - Optional notes
 */
export const updateConnectionStatus = async (connectionId, status, adminNotes = '') => {
  console.log('[connections.api] 📝 Calling: PUT /api/connections/:id/status', {
    connectionId,
    status,
    notesLength: adminNotes.length
  });

  try {
    const response = await apiClient.put(`/connections/${connectionId}/status`, {
      status,
      adminNotes
    });
    console.log('[connections.api] ✅ Connection status updated:', {
      newStatus: response.data.connection?.status
    });
    return response.data;
  } catch (error) {
    console.error('[connections.api] ❌ Failed to update status:', error.response?.data);
    throw error;
  }
};

// ============================================
// SECTION: FETCH USER CONNECTIONS
// ============================================

/**
 * Get all connections for authenticated user (landowner or investor)
 */
export const getUserConnections = async () => {
  console.log('[connections.api] 👤 Calling: GET /api/connections/my');

  try {
    const response = await apiClient.get('/connections/my');
    console.log('[connections.api] ✅ User connections fetched:', {
      count: response.data.connections?.length
    });
    return response.data;
  } catch (error) {
    console.error('[connections.api] ❌ Failed to fetch user connections:', error.response?.data);
    throw error;
  }
};

// ============================================
// EXPORTS
// ============================================

export const connectionsAPI = {
  connectParties,
  getAllConnections,
  getConnectionDetails,
  updateConnectionStatus,
  getUserConnections
};

export default connectionsAPI;